import {Component, Input, OnInit} from '@angular/core';
import { Location } from '@angular/common';
import { ROOT_ORGS } from '../capd-admin-component/capd-admin.component';

import {CDSNode} from '../services/capd.model';
import { CDSTreeService } from '../services/cds.service';
import {UserService} from "../services/user.service";
import {HttpClient} from "@angular/common/http";
import {OrgService} from "../services/org.service";
import {OrgDataService} from "../services/org-data.service";
import {Router} from "@angular/router";
import {ExportToCsv} from "export-to-csv";
import {AlertService} from "../services/alert.service";

@Component({
  selector: 'cds-tree',
  templateUrl: './cds-tree.component.html',
  inputs: [ 'currentNode' ]
})
/**
 * This function displays the CDS Tree.  It is called recursively for each node created, and
 * each function controls data for its specific node.
 */
export class CDSTreeComponent implements OnInit {

  scope = '';
  @Input() currentNode: CDSNode;
  isActive = false;
  generatingExport = false

  constructor(private _service: CDSTreeService,
              private _userService: UserService,
              private _orgService: OrgService,
              private http: HttpClient,
              private _orgDataService: OrgDataService,
              private _router: Router,
              private _location: Location,
              private alertService: AlertService) { }

  /**
   * Adds numbers to the two number fields of the node, when created, if it's necessary to do so.
   *
   * @func ngOnInit
   */
  ngOnInit() {
    // Get the scope of the session.  If the user is logged in at a company, and the current node
    // is a company level-one, then automatically expand it.  Otherwise, leave it collapsed.
    this.scope = sessionStorage.getItem('scope');

    // If either "numberOfAuthorsOrMtsos" or "contentPackages" is undefined, set them to zero.
    if (!this.currentNode.numberOfAuthorsOrMtsos) {
      this.currentNode.numberOfAuthorsOrMtsos = 0;
    }
  }

  /**
   * Toggles showing the children of a given node.
   *
   * @async
   * @func toggleTree
   */
  async toggleTree() {
    this.isActive = !this.isActive;

    // Escape case; nothing needs to be done if there are already children
    // OR if the node is marked Inactive
    if (!this.isActive || (this.currentNode.children !== undefined && this.currentNode.children !== null) ) { return; }

    // Grab all of the node's non-group children
    await this._service.getTreeChildren(this.currentNode).then((children: CDSNode[]) => {
      this.currentNode.children = this.filterStructuralChildren(children);
    }).catch(() => {console.log('caught'); this.currentNode.children = null; this.isActive = false; });

    this.currentNode.children = this.sortedChildren(this.currentNode.children)
    return this.currentNode;


  }

  /**
   * Refreshes the node, grabbing its groups and children a second time.
   *
   * @async
   * @func refreshNode
   */
  async refreshNode() {
    // Empty the list of children
    this.currentNode.children = undefined;
    this.currentNode.groupChildren = undefined;
    let branchRefresh = null;
    

    // Activate node
    if (!this.isActive) {
      this.isActive = true;
    }

    // call our backend endpoint that clears the users cache & the cds tree cache
    this._service.refreshBranch(this.currentNode)
      .then((response: CDSNode[]) => {
        branchRefresh = response;
      })
      .catch((error) => {
        console.log('[CDSTreeComponent] FAILURE refreshBranch, falling back to refreshNodeBranch', error);
      });

    // Refresh the groups
    if (this.currentNode.distanceFromRoot >= 1) {
      this._service.getGroupsFromCDSWebAPI(this.currentNode)
        .then((groupSet: CDSNode[]) => {
          return this.refreshNodeGroups();
        })
        .catch((error) => {
          console.error('[CDSTreeComponent] FAILURE getGroupsFromCDSWebAPI', error);
        });
    }

    // Replace list of children with updated children
    await this._service.getTreeChildren(this.currentNode).then((children: CDSNode[]) => {
      this.currentNode.children = this.filterStructuralChildren(children);
    }).catch((error) => {
      console.error('[CDSTreeComponent] FAILURE getTreeChildren', error);
      this.currentNode.children = null;
      this.isActive = false;
    });

    this.currentNode.children = this.sortedChildren(this.currentNode.children);

  }

  async refreshNodeGroups() {
    // Refresh the groups
      await this._service.refreshGroups(this.currentNode).then((res: CDSNode[]) => {
        // @ts-ignore
        if(res.Groups.listGroupsResponse.listGroupsReturn) {
          // @ts-ignore
          const groupedUsers = res.Users.UserList.reduce((group, user) => {
            user.groupShortOids.forEach(oid => {
              group[oid] = group[oid] ?? []
              group[oid].push(user)
            })

            return group;
          }, {})

          // @ts-ignore
          res.Groups.listGroupsResponse.listGroupsReturn.forEach((group: CDSNode) => {
            // 123 and Capd User Settings are commonly returned from the root level, but should not be used as groups
            // @ts-ignore
            if (group.externalId != "2.16.840.1.113883.3.21/7~Author_SettingsGroup_31302" && group.externalId != "2.16.840.1.113883.3.21/7~Author_SettingsGroup_31042") {
              if (ROOT_ORGS.indexOf(group.rootId) >= 0) {
                group.distanceFromRoot = 0;
              } else {
                group.distanceFromRoot = this.currentNode.distanceFromRoot + 1;
              }
              // @ts-ignore
              group.description = group.name.entityNamePart[0].text
              group.type = "SettingsGroup"
              // @ts-ignore
              group.rootId = group.objectId.root
              group.rootId = group.rootId.replace('2.16.840.1.113883.3.21', '~')
              // @ts-ignore
              group.extensionId = group.objectId.extension

              var shortOid = group.rootId + "/" + group.extensionId
              if(groupedUsers[shortOid])
                group.numberOfAuthorsOrMtsos = groupedUsers[shortOid].length
              else
                group.numberOfAuthorsOrMtsos = 0

              // @ts-ignore
              this.currentNode.groupChildren = res.Groups.listGroupsResponse.listGroupsReturn
            }
          })
        }
        return this.currentNode.groupChildren
      })
          .catch((error) => {
            console.error('[CDSTreeComponent] FAILURE refreshNodeGroups', error);
            this.currentNode.groupChildren = null;
          });
    }

  refreshNodeBranch() {
    // Refresh the non group structural nodes
    return this._service.getTreeChildren(this.currentNode).then((children: CDSNode[]) => {
      this.currentNode.children = this.filterStructuralChildren(children, true);
      if(this.currentNode.children.length > 0)
        this.currentNode.hasStructuralChildren = true;
      this.currentNode.children.forEach(child => {
        if (ROOT_ORGS.indexOf(child.rootId) >= 0) { child.distanceFromRoot = 0; }
        else { child.distanceFromRoot = this.currentNode.distanceFromRoot + 1; }
        child.groupChildren = children.filter(groupChild =>
            groupChild.type === 'SettingsGroup' && groupChild.rootId === child.rootId);
        child.groupChildren.forEach(groupChild => {
          if (ROOT_ORGS.indexOf(groupChild.rootId) >= 0) { groupChild.distanceFromRoot = 0; }
          else { groupChild.distanceFromRoot = child.distanceFromRoot + 1; }
        });
      });
    })
  }

  private filterStructuralChildren(children: CDSNode[], excludeSettingsGroups: boolean = false): CDSNode[] {
    if (!children) {
      return [];
    }

    // Preserve pre-ECHO-13624 matching: CDS nodes are identified by rootId prefixing.
    return children.filter((child: any) =>
      child.rootId &&
      child.rootId.includes(this.currentNode.rootId) &&
      (!excludeSettingsGroups || child.type !== 'SettingsGroup'));
  }
  
  /**
   * Sorts children alphabetically.
   *
   * @func sortedChildren
   * @param {CDSNode[]} children - a list of children to sort.
   * @returns {CDSNode[]} the sorted list of children.
   */
  sortedChildren(children: CDSNode[]) {
    if (!children) {
      return null;
    }

    let groupChildren = children.filter(x => x.type == 'SettingsGroup')
    let nonGroupChildren = children.filter(x => x.type != 'SettingsGroup')

    let res = [ ...groupChildren, ...nonGroupChildren]

    return res
  }

  /**
   * Aggregates all the names of packages into a single string.
   *
   * @func printPackageNames
   * @returns {string} a formatted string of the package list.
   */
  printPackageNames(): string {
    let temp = '';
    if (this.currentNode.contentPackages && this.currentNode.contentPackages.length > 0){
      this.currentNode.contentPackages.forEach(pack => {
        temp += pack.contentPackage_id + '\n';
      });
    }
    else {
      temp = 'This customer has no assigned packages.';
    }

    return temp;
  }

  public async updateCdsTreePackageCount(org: CDSNode) {
    this.currentNode = org
      if(this.currentNode.numberOfPackages) {
        if (document.getElementById("package-count-" + this.currentNode.rootId)) {
          document.getElementById("package-count-" + this.currentNode.rootId).innerHTML = this.currentNode.numberOfPackages.toString() + " <i class=\"ai-16 ai-archive\"></i>"
        }
      }
  }

  setSelectedNode() {
    if(this.currentNode.type == "SettingsGroup" && this.isClassicView()) {
      this._router.navigateByUrl(this._location.path().replace("/settings", "/grouprules"));
    } else if(this.currentNode.type == "SettingsGroup" && !this.isClassicView()) {
      this._router.navigateByUrl(this._location.path().replace("/settings", "/grouprules/v2"));
    }
    this._orgDataService.setCurrentOrg(this.currentNode);
  }

  isClassicView() {
    var version = sessionStorage.getItem('uiVersion')
    if (version == 'Classic')
      return true;
    else
      return false;
  }

  getRootRouterLink() {
    if (this.isClassicView())
      return "mmodal";
    else
      return "mmodal/v2/library";
  }

  async getOrgsExport() {
    this.generatingExport = true

    const options = {
      filename: 'Org_Oids',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      title: 'All Orgs Oids',
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true
    };

    const csvExporter = new ExportToCsv(options);
    let exportData = [];

    await this._orgService.getAllOrgs(sessionStorage.getItem('rootId')).then(res => {
      res.forEach((org: CDSNode) => {
        const orgExportVal = {
          'Name': org.description,
          'Extension': org.extensionId,
          'Node Type': org.type,
          'Root ID': org.rootId
        };
        exportData.push(orgExportVal);
      })
    })

    if (!(exportData == undefined)) {
      csvExporter.generateCsv(exportData);
    } else {
      this.alertService.showAlert("Invalid Export. No data provided.");
    }
    this.generatingExport = false

  }

}
